/**
 * The shared engine wiring for in-place ops ({@link InPlaceEditDeps}): the
 * SAME calls the toolbar's post-processing buttons make through
 * `useRoiSdfOperation` — `roi.replaceTexture(result, label)`, a
 * contour-layer view update, and the same green success snackbar — and the
 * uniform release / preview overlay /
 * approval / edit-version reads. Each op's hook calls this PLAIN function
 * inside its own `useCallback` and adds only its engine call. The engine is
 * a SYSTEM BOUNDARY: verified by the manual demo; the logic it feeds is
 * unit-tested through fakes.
 */
import type { Environment } from '~/engine/data/Environment';
import type { ContourTexture } from '~/engine/data/structure/ContourTexture';
import type { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { enqueueSnackbar } from '~/utils/notifications';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { ViewManager } from '~/engine/view/ViewManager';
import { projectStructureFacts } from './engineStructureFacts';
import type { InPlaceEditDeps } from './inPlaceEdit';
import type { ComputedGeometry, StructureFacts } from './pipeline';
import { createPreviewOverlayPort } from './previewOverlay';

export function makeInPlaceEngineDeps(
    env: Environment | null,
    structureSet: StructureSetReadModel | undefined,
    /** The toolbar button's undo-history label, e.g. 'Fill Cavities'. */
    operationName: string,
    /** The toolbar button's success snackbar text for this result. */
    successMessage: (roiName: string, computed: ComputedGeometry) => string
): InPlaceEditDeps & { readonly structures: ReadonlyArray<StructureFacts> } {
    const overlay = createPreviewOverlayPort(env);
    return {
        structures: projectStructureFacts(structureSet, env),
        canModify: (roiNumber) => env?.getRoi(roiNumber)?.canModify ?? false,
        // Read live: `env` is the mutable singleton.
        currentContextId: () => env?.structureSet?.sopInstanceUID,
        editVersion: (roiNumber) => env?.getRoi(roiNumber)?.sdfVersion,
        geometry: {
            releaseComputed: (handle) => {
                (handle as ContourTexture | undefined)?.delete();
            },
        },
        commit: {
            replaceInPlace: (roiNumber, computed) => {
                const roi = env?.getRoi(roiNumber);
                const set = env?.structureSet;
                if (!roi || !set) {
                    throw new Error('the structure is no longer loaded');
                }
                // Exactly what useRoiSdfOperation does for the buttons.
                roi.replaceTexture(
                    computed.handle as ContourTexture,
                    operationName
                );
                ViewManager.viewUpdateEvent(
                    new VolumeViewUpdateEvent(
                        set,
                        LayerBit.CONTOUR_LAYER_BIT,
                        true
                    )
                );
                enqueueSnackbar(successMessage(roi.name, computed), {
                    variant: 'success',
                });
                return roi.volumeCm3;
            },
        },
        preview: {
            show: (computed) => overlay.show(computed.handle as ContourTexture),
            hide: () => overlay.hide(),
        },
    };
}
